import React from 'react';
import { DonationCampaign } from '../../types';
import { Heart, Users, Calendar, Share2, Sparkles, Edit3, ArrowRight } from 'lucide-react';
import { formatCurrencyMYR } from '../../utils/formatters';

interface DonationCardProps {
  campaign: DonationCampaign;
  onDonate: (campaign: DonationCampaign) => void;
  onShare: (campaign: DonationCampaign) => void;
  onEdit?: (campaign: DonationCampaign) => void;
  isManagement?: boolean;
  featured?: boolean;
}

export const DonationCard: React.FC<DonationCardProps> = ({
  campaign,
  onDonate,
  onShare,
  onEdit,
  isManagement = false,
  featured = false,
}) => {
  const percent = Math.min(100, Math.round((campaign.currentAmount / (campaign.targetAmount || 1)) * 100));

  const getDaysLeft = (): string => {
    if (!campaign.endDate) return 'Berterusan';
    const end = new Date(campaign.endDate).getTime();
    const now = new Date().getTime();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'Tamat';
    if (diff === 0) return 'Hari Terakhir';
    return `${diff} hari lagi`;
  };

  const getCategoryBadgeClass = (cat: string) => {
    switch (cat) {
      case 'Pembangunan Gelanggang':
        return 'bg-amber-50 text-amber-700 border-amber-200/70';
      case 'Kebajikan & Asnaf':
        return 'bg-red-50 text-red-700 border-red-200/70';
      case 'Peralatan & Busana':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'Kontinjen & Sukan':
        return 'bg-blue-50 text-blue-700 border-blue-200/70';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  if (featured) {
    return (
      <div className="bg-white text-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 space-y-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="space-y-3 max-w-2xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-700 text-[11px] font-extrabold rounded-full border border-red-100 shadow-2xs">
                <Sparkles className="w-3.5 h-3.5 text-red-600" />
                Pilihan Utama
              </span>
              <span className={`px-3 py-1 text-[11px] font-bold rounded-full border ${getCategoryBadgeClass(campaign.category)}`}>
                {campaign.category}
              </span>
              <span className="text-xs text-slate-500 font-medium">
                • {campaign.beneficiaryGelanggang === 'Semua Gelanggang' ? 'Semua Gelanggang' : (campaign.beneficiaryGelanggang ? `Gelanggang ${campaign.beneficiaryGelanggang}` : 'Semua Gelanggang')}
              </span>
            </div>

            <h3 
              onClick={() => onDonate(campaign)}
              className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight leading-snug cursor-pointer hover:text-red-600 transition-colors"
              title="Lihat butiran kempen"
            >
              {campaign.title}
            </h3>

            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-2">
              {campaign.story}
            </p>
          </div>

          {/* Action buttons with high-contrast pills */}
          <div className="flex items-center gap-2.5 w-full lg:w-auto shrink-0 flex-wrap">
            {isManagement && onEdit && (
              <button
                type="button"
                onClick={() => onEdit(campaign)}
                className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-full transition-all border border-slate-200/80 shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                title="Kemaskini"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Urus</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => onShare(campaign)}
              className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-full transition-all border border-slate-200/80 shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span>Kongsi</span>
            </button>

            <button
              type="button"
              onClick={() => onDonate(campaign)}
              className="flex-1 sm:flex-initial px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs sm:text-sm rounded-full transition-all shadow-md shadow-red-600/20 hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer group"
            >
              <Heart className="w-4 h-4 fill-white group-hover:scale-110 transition-transform" />
              <span>Sumbang Infaq</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>

        {/* Progress Visualizer */}
        <div className="bg-slate-50/70 p-5 sm:p-6 rounded-2xl border border-slate-100 space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <span className="text-2xl sm:text-3xl font-black text-slate-900">
                {formatCurrencyMYR(campaign.currentAmount)}
              </span>
              <span className="text-xs text-slate-500 font-medium ml-2">
                daripada sasaran <strong>{formatCurrencyMYR(campaign.targetAmount)}</strong>
              </span>
            </div>

            <div className="flex items-center gap-4 text-xs font-semibold text-slate-600">
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-slate-500" />
                <span><strong>{campaign.donorCount}</strong> penderma</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span>{getDaysLeft()}</span>
              </span>
              <span className="text-xs font-extrabold text-red-700 bg-red-50 border border-red-200/60 px-2.5 py-0.5 rounded-full">
                {percent}%
              </span>
            </div>
          </div>

          {/* Gradient Progress Bar */}
          <div className="w-full bg-slate-200/80 h-3 rounded-full overflow-hidden">
            <div
              className="bg-gradient-to-r from-red-600 to-amber-500 h-full rounded-full transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Standard Card view for grids
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between overflow-hidden">
      <div className="p-6 space-y-4">
        {/* Badges & Meta */}
        <div className="flex items-center justify-between gap-2">
          <span className={`px-3 py-1 text-[10px] font-bold rounded-full border ${getCategoryBadgeClass(campaign.category)}`}>
            {campaign.category}
          </span>
          <span className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {getDaysLeft()}
          </span>
        </div>

        {/* Title & Story */}
        <div className="space-y-1.5">
          <h4 
            onClick={() => onDonate(campaign)}
            className="font-extrabold text-base sm:text-lg text-slate-900 line-clamp-2 cursor-pointer hover:text-red-600 transition-colors leading-snug"
            title="Lihat butiran kempen"
          >
            {campaign.title}
          </h4>
          <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
            {campaign.story}
          </p>
        </div>

        {/* Progress Bar & Numbers */}
        <div className="space-y-2 pt-2">
          <div className="flex items-baseline justify-between text-xs">
            <span className="font-black text-base text-slate-900">
              {formatCurrencyMYR(campaign.currentAmount)}
            </span>
            <span className="text-slate-500 font-semibold">
              <strong className="text-slate-900">{percent}%</strong> ({campaign.donorCount} penderma)
            </span>
          </div>

          <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
            <div
              className="bg-gradient-to-r from-red-600 to-amber-500 h-full rounded-full transition-all duration-500"
              style={{ width: `${percent}%` }}
            />
          </div>

          <div className="text-[11px] text-slate-500">
            Sasaran: <strong className="text-slate-700">{formatCurrencyMYR(campaign.targetAmount)}</strong>
          </div>
        </div>
      </div>

      {/* Footer Actions with Pill Buttons */}
      <div className="p-4 bg-slate-50/70 border-t border-slate-100 flex items-center gap-2">
        {isManagement && onEdit && (
          <button
            type="button"
            onClick={() => onEdit(campaign)}
            className="p-2.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200/80 rounded-full shadow-2xs transition-all cursor-pointer"
            title="Kemaskini"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
        )}

        <button
          type="button"
          onClick={() => onShare(campaign)}
          className="p-2.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200/80 rounded-full shadow-2xs transition-all cursor-pointer"
          title="Kongsi pautan"
        >
          <Share2 className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => onDonate(campaign)}
          className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs rounded-full shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer group"
        >
          <Heart className="w-3.5 h-3.5 fill-white" />
          <span>Sumbang</span>
          <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  );
};
